import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { Mail, Sparkles, ArrowLeft, ShieldCheck, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { API_URL } from "../../config";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);
  const history = useHistory();

  const onSubmit = async (event) => {
    event.preventDefault();
    setMessage(null);
    setMessageType(null);

    if (!email) {
      setMessage("Please enter your email.");
      setMessageType("error");
      return;
    }

    setIsLoading(true);

    try {
      const res = await axios.post(
        `${API_URL}/api/freshment_portal_apis/College/forgot_password?college_email=${email}`,
      );

      setIsLoading(false);

      if (res.data.success) {
        setMessage(res.data.message);
        setMessageType("success");
        setTimeout(() => {
          history.push("/reset-password");
        }, 1500);
      } else {
        setMessage(res.data.message);
        setMessageType("error");
      }
    } catch (error) {
      setIsLoading(false);
      setMessage("An error occurred. Please try again later.");
      setMessageType("error");
    }
  };

  return (
    <div className="position-relative d-flex align-items-center justify-content-center overflow-hidden" style={styles.page}>
      {/* Background blobs */}
      <div className="position-absolute" style={styles.blobTop} />
      <div className="position-absolute" style={styles.blobBottom} />

      <div className="w-100 bg-white rounded-4 shadow position-relative d-flex flex-column gap-4" style={styles.card}>
        <div className="text-center">
          <div className="mx-auto d-flex align-items-center justify-content-center rounded-4 shadow-sm mb-3" style={styles.iconBox}>
            <Sparkles style={{ width: "24px", height: "24px", color: "#FF5F14" }} />
          </div>
          <h1 className="fw-bold font-display" style={styles.heading}>Recover Password</h1>
          <p className="mt-2" style={styles.subtext}>
            Input your registered academic or corporate email address below. We'll send security reset parameters.
          </p>
        </div>

        {message && (
          <div style={messageType === "success" ? styles.successBox : styles.errorBox}>
            <div className="d-flex align-items-start gap-2">
              {messageType === "success" ? (
                <CheckCircle2 style={{ width: "16px", height: "16px", color: "#059669", flexShrink: 0, marginTop: "2px" }} />
              ) : (
                <XCircle style={{ width: "16px", height: "16px", color: "#dc2626", flexShrink: 0, marginTop: "2px" }} />
              )}
              <span style={{ fontSize: "0.75rem", color: messageType === "success" ? "#065f46" : "#991b1b" }}>{message}</span>
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="d-flex flex-column gap-3">
          <div className="d-flex flex-column gap-2">
            <label className="fw-bold d-block" style={styles.label}>Registered Email Address</label>
            <div className="position-relative">
              <span className="position-absolute top-0 bottom-0 start-0 d-flex align-items-center" style={styles.inputIconWrap}>
                <Mail style={{ width: "16px", height: "16px" }} />
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@university.edu"
                className="w-100 fw-medium forgot-input"
                style={styles.input}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-100 orangeBtn d-flex align-items-center justify-content-center gap-2 text-uppercase fw-bold border-0"
            style={{ ...styles.submitButton, opacity: isLoading ? 0.5 : 1 }}
          >
            {isLoading ? (
              <>
                <div style={styles.spinner} />
                <span>Requesting Recovery Token...</span>
              </>
            ) : (
              <span>Send Recovery Email</span>
            )}
          </button>
        </form>

        <div className="d-flex align-items-center justify-content-center pt-2">
          <a
            href="/login"
            className="d-flex orange-color-hover align-items-center gap-2 fw-semibold text-decoration-none"
            style={styles.backLink}
          >
            <ArrowLeft style={{ width: "16px", height: "16px" }} />
            <span>Return to Sign In</span>
          </a>
        </div>

        {/* Branding Footer */}
        <div className="d-flex align-items-center justify-content-center gap-2 text-uppercase fw-bold" style={styles.footer}>
          <ShieldCheck style={{ width: "14px", height: "14px", color: "#059669" }} />
          <span className="font-mono fw-bold">EduConnectPro Secured Area</span>
        </div>
      </div>

      <style>{`
        @keyframes forgotSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .forgot-input:focus {
          outline: none;
          border-color: #FF5F14 !important;
          box-shadow: 0 0 0 1px #FF5F14 !important;
        }
      `}</style>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "85vh",
    backgroundColor: "#f8fafc",
    color: "#0f172a",
    fontFamily: "sans-serif",
    padding: "3rem 1rem"
  },
  blobTop: {
    top: "40px",
    left: "40px",
    width: "288px",
    height: "288px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 95, 20, 0.05)",
    filter: "blur(100px)",
    pointerEvents: "none"
  },
  blobBottom: {
    bottom: "40px",
    right: "40px",
    width: "384px",
    height: "384px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 95, 20, 0.03)",
    filter: "blur(120px)",
    pointerEvents: "none"
  },
  card: {
    maxWidth: "28rem",
    padding: "2rem",
    border: "1px solid #e2e8f0",
    zIndex: 10,
    borderRadius: "1rem"
  },
  iconBox: {
    width: "48px",
    height: "48px",
    backgroundColor: "rgba(255, 95, 20, 0.1)",
    border: "1px solid rgba(255, 95, 20, 0.2)",
    borderRadius: "1rem"
  },
  heading: {
    fontSize: "1.75rem",
    color: "#020617"
  },
  subtext: {
    fontSize: "0.75rem",
    color: "#64748b"
  },
  label: {
    fontSize: "0.75rem",
    color: "#334155"
  },
  inputIconWrap: {
    paddingLeft: "0.875rem",
    color: "#94a3b8"
  },
  input: {
    paddingLeft: "2.5rem",
    paddingRight: "1rem",
    paddingTop: "0.75rem",
    paddingBottom: "0.75rem",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    fontSize: "0.875rem",
    color: "#0f172a",
    borderRadius: "0.75rem"
  },
  submitButton: {
    padding: "0.75rem 1rem",
    borderRadius: "0.75rem",
    backgroundColor: "#FF5F14",
    color: "#fff",
    fontSize: "0.75rem",
    letterSpacing: "0.05em",
    cursor: "pointer",
    boxShadow: "0 10px 15px -3px rgba(255, 95, 20, 0.1)"
  },
  spinner: {
    width: "16px",
    height: "16px",
    border: "2px solid rgba(255,255,255,0.3)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "forgotSpin 0.8s linear infinite"
  },
  backLink: {
    fontSize: "0.75rem",
    color: "#475569"
  },
  footer: {
    paddingTop: "1rem",
    borderTop: "1px solid #f1f5f9",
    fontSize: "10px",
    fontFamily: "monospace",
    color: "#94a3b8",
    letterSpacing: "0.1em"
  },
  successBox: {
    padding: "0.875rem",
    backgroundColor: "#ecfdf5",
    border: "1px solid #a7f3d0",
    fontSize: "0.75rem",
    borderRadius: "0.5rem"
  },
  errorBox: {
    padding: "0.875rem",
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    fontSize: "0.75rem",
    borderRadius: "0.5rem"
  }
};